import React, { useEffect, useState, useCallback } from "react";
import { useI18n } from "../../lib/i18n";
import { apiClient } from "../../lib/api";
import {
  MessageSquare, MessageCircle, Megaphone, RefreshCw,
} from "lucide-react";

/**
 * iter231 — Official CARTAL Channel: fully self-contained admin tab.
 *
 * Consumes the EXISTING backend endpoints (no backend changes):
 *   GET  /admin/official-conversations/templates
 *   GET  /admin/official-conversations/analytics
 *   POST /admin/official-conversations/{user_id}
 *   POST /admin/official-conversations/broadcast
 *   GET  /admin/users              (for target selection)
 *
 * This tab is a standalone workspace that intentionally does NOT modify
 * anything on the Users tab. All CARTAL-specific UI (templates, broadcast,
 * analytics, per-user open-chat, WhatsApp shortcut) lives here.
 */
export default function CartalChannelTab() {
  const { lang } = useI18n();

  // Users + selection
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  // Templates + picked template
  const [templates, setTemplates] = useState([]);
  const [tplKey, setTplKey] = useState("welcome");

  // Analytics
  const [analytics, setAnalytics] = useState(null);

  // Async flags
  const [msgingId, setMsgingId] = useState(null);
  const [broadcasting, setBroadcasting] = useState(false);

  const loadUsers = useCallback(() => {
    apiClient.get("/admin/users", { params: { q } })
      .then(({ data }) => setUsers(data.items || []))
      .catch(() => setUsers([]));
  }, [q]);

  const loadTemplates = useCallback(() => {
    apiClient.get("/admin/official-conversations/templates")
      .then(({ data }) => setTemplates(data.items || []))
      .catch(() => setTemplates([]));
  }, []);

  const loadAnalytics = useCallback(() => {
    apiClient.get("/admin/official-conversations/analytics")
      .then(({ data }) => setAnalytics(data))
      .catch(() => setAnalytics(null));
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadTemplates(); loadAnalytics(); }, [loadTemplates, loadAnalytics]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (prev.size === users.length) return new Set();
      return new Set(users.map((u) => u.id));
    });
  };

  // Open (or reuse) the idempotent CARTAL thread with a single user
  // and post the CURRENTLY SELECTED template as the next message.
  const openOfficialChat = async (id) => {
    if (msgingId) return;
    setMsgingId(id);
    try {
      const { data } = await apiClient.post(
        `/admin/official-conversations/${id}`,
        { template: tplKey },
      );
      const convId = data?.conversation_id || data?.conversation?.id;
      loadAnalytics();
      if (convId) window.location.href = `/messages?c=${convId}`;
    } catch (e) {
      alert(lang === "ar" ? "تعذّر فتح المحادثة الرسمية" : "Failed to open official chat");
    } finally {
      setMsgingId(null);
    }
  };

  const broadcastSelected = async () => {
    if (broadcasting || selected.size === 0) return;
    const label = templates.find((t) => t.key === tplKey)?.label_ar || tplKey;
    if (!window.confirm(
      lang === "ar"
        ? `إرسال قالب "${label}" رسمياً باسم CARTAL إلى ${selected.size} مستخدم؟`
        : `Send "${label}" as official CARTAL message to ${selected.size} users?`,
    )) return;
    setBroadcasting(true);
    try {
      const { data } = await apiClient.post(
        "/admin/official-conversations/broadcast",
        { user_ids: Array.from(selected), template: tplKey },
      );
      alert(lang === "ar"
        ? `تم الإرسال بنجاح إلى ${data.sent} مستخدم (${data.created_threads} محادثة جديدة، ${data.reused_threads} محادثة قائمة).`
        : `Sent to ${data.sent} users (${data.created_threads} new / ${data.reused_threads} reused).`);
      setSelected(new Set());
      loadAnalytics();
    } catch (e) {
      alert(lang === "ar" ? "فشل البث الرسمي" : "Broadcast failed");
    } finally {
      setBroadcasting(false);
    }
  };

  // Manual WhatsApp deep-link — pre-fills the selected template body.
  // Requires the user to have a phone on file. No external API cost.
  const openWhatsApp = (u) => {
    if (!u.phone) {
      alert(lang === "ar" ? "لا يوجد رقم جوال لهذا المستخدم" : "No phone on file for this user");
      return;
    }
    const digits = String(u.phone).replace(/[^0-9]/g, "");
    const body = templates.find((t) => t.key === tplKey)?.body || "";
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(body)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const sentiment = analytics?.sentiment || { positive: 0, neutral: 0, negative: 0 };
  const currentBody = templates.find((t) => t.key === tplKey)?.body || "";

  return (
    <div data-testid="cartal-channel-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#8a6f2e] text-xs uppercase tracking-widest font-semibold">
            <MessageSquare className="w-4 h-4" />
            {lang === "ar" ? "قناة رسمية" : "Official Channel"}
          </div>
          <h2 className="font-display text-2xl mt-1">
            {lang === "ar" ? "قناة CARTAL الرسمية" : "Official CARTAL Channel"}
          </h2>
          <p className="text-xs text-[#6b6b6b] mt-1 max-w-2xl">
            {lang === "ar"
              ? "قناة تواصل مباشرة من إدارة CARTAL إلى أي مستخدم أو معلن لجمع الملاحظات وحلّ الشكاوى. لا تؤثر على «راسل البائع» أو «تواصل معنا»."
              : "Direct outreach channel from CARTAL admin to any user/advertiser for feedback and complaint resolution. Does not affect Contact Seller or Contact Us."}
          </p>
        </div>
        <button
          onClick={() => { loadAnalytics(); loadUsers(); }}
          className="btn-ghost text-sm"
          data-testid="cartal-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {lang === "ar" ? "تحديث" : "Refresh"}
        </button>
      </div>

      {/* Analytics dashboard */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="cartal-analytics">
          <div className="surface rounded-xl p-4 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[#8a8a8a]">
              {lang === "ar" ? "محادثات رسمية" : "Threads"}
            </div>
            <div className="font-display text-2xl text-[#0a0a0a] mt-1">{analytics.total_threads}</div>
          </div>
          <div className="surface rounded-xl p-4 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[#8a6f2e]">
              {lang === "ar" ? "معدل الردّ" : "Reply rate"}
            </div>
            <div className="font-display text-2xl text-[#8a6f2e] mt-1">{analytics.response_rate_pct}%</div>
          </div>
          <div className="surface rounded-xl p-4 text-center">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700">
              {lang === "ar" ? "إيجابية" : "Positive"}
            </div>
            <div className="font-display text-2xl text-emerald-700 mt-1">{sentiment.positive}</div>
          </div>
          <div className="surface rounded-xl p-4 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[#8a8a8a]">
              {lang === "ar" ? "محايدة" : "Neutral"}
            </div>
            <div className="font-display text-2xl text-[#525252] mt-1">{sentiment.neutral}</div>
          </div>
          <div className="surface rounded-xl p-4 text-center">
            <div className="text-[10px] uppercase tracking-widest text-red-700">
              {lang === "ar" ? "سلبية" : "Negative"}
            </div>
            <div className="font-display text-2xl text-red-700 mt-1">{sentiment.negative}</div>
          </div>
        </div>
      )}

      {/* Template picker + broadcast bar */}
      <div className="surface rounded-xl p-4 mb-5 border border-[#c9a55c]/30 bg-[#fdf9ef]" data-testid="cartal-toolbar">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <label className="text-[10px] uppercase tracking-widest text-[#8a6f2e] font-semibold">
              {lang === "ar" ? "قالب الرسالة" : "Message template"}
            </label>
            <select
              className="input-dark text-sm mt-1 w-full"
              value={tplKey}
              onChange={(e) => setTplKey(e.target.value)}
              data-testid="cartal-template-select"
            >
              {templates.map((t) => (
                <option key={t.key} value={t.key}>{t.label_ar}</option>
              ))}
            </select>
            {currentBody && (
              <div className="text-[12px] text-[#525252] mt-2 leading-6 whitespace-pre-wrap border-s-2 border-[#c9a55c]/50 ps-3">
                {currentBody}
              </div>
            )}
          </div>
          <div className="flex flex-col items-stretch gap-2">
            <button
              onClick={broadcastSelected}
              disabled={broadcasting || selected.size === 0}
              className="btn-primary text-xs disabled:opacity-50"
              data-testid="cartal-broadcast-btn"
            >
              <Megaphone className="w-3 h-3" />
              {broadcasting
                ? (lang === "ar" ? "جارٍ البث…" : "Broadcasting…")
                : (lang === "ar" ? `بث رسمي (${selected.size})` : `Broadcast (${selected.size})`)}
            </button>
            <div className="text-[11px] text-[#8a8a8a] text-center">
              {lang === "ar"
                ? `${selected.size} مستخدم محدَّد`
                : `${selected.size} user${selected.size === 1 ? "" : "s"} selected`}
            </div>
          </div>
        </div>
      </div>

      {/* Target picker */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-display text-lg">
          {lang === "ar" ? "اختر المستقبِلين" : "Pick recipients"} ({users.length})
        </h3>
        <input
          className="input-dark max-w-xs"
          placeholder={lang === "ar" ? "بحث…" : "Search…"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="cartal-users-search"
        />
      </div>

      <div className="surface rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a]/[0.04] text-[#3a3a3a] text-xs uppercase tracking-widest">
            <tr>
              <th className="p-3 text-start w-8">
                <input
                  type="checkbox"
                  checked={users.length > 0 && selected.size === users.length}
                  onChange={toggleSelectAll}
                  aria-label={lang === "ar" ? "تحديد الكل" : "Select all"}
                  data-testid="cartal-select-all"
                />
              </th>
              <th className="p-3 text-start">{lang === "ar" ? "الاسم" : "Name"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "البريد" : "Email"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "الدور" : "Role"}</th>
              <th className="p-3 text-end">{lang === "ar" ? "إجراء" : "Action"}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#0a0a0a]/[0.06]" data-testid={`cartal-user-${u.id}`}>
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggleSelect(u.id)}
                    aria-label={`select ${u.name}`}
                    data-testid={`cartal-select-${u.id}`}
                  />
                </td>
                <td className="p-3">
                  {u.name}
                  {u.company_name && <span className="text-[#8a8a8a] block text-xs">{u.company_name}</span>}
                </td>
                <td className="p-3 text-[#3a3a3a]">{u.email}</td>
                <td className="p-3"><span className="pill text-[10px]">{u.role}</span></td>
                <td className="p-3">
                  <div className="flex justify-end gap-2 flex-wrap">
                    <button
                      onClick={() => openOfficialChat(u.id)}
                      disabled={msgingId === u.id}
                      className="btn-ghost text-xs"
                      data-testid={`cartal-message-${u.id}`}
                      title={lang === "ar" ? "افتح قناة تواصل رسمية من CARTAL بالقالب المحدد" : "Open official CARTAL chat with selected template"}
                    >
                      <MessageSquare className="w-3 h-3" />
                      {msgingId === u.id
                        ? (lang === "ar" ? "جارٍ الفتح…" : "Opening…")
                        : (lang === "ar" ? "مراسلة" : "Message")}
                    </button>
                    {u.phone && (
                      <button
                        onClick={() => openWhatsApp(u)}
                        className="btn-ghost text-xs text-emerald-700 border-emerald-300"
                        data-testid={`cartal-whatsapp-${u.id}`}
                        title={lang === "ar" ? "فتح واتساب مع القالب المحدد" : "Open WhatsApp with selected template"}
                      >
                        <MessageCircle className="w-3 h-3" />
                        WhatsApp
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
